import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink, Info, Lock, X } from "lucide-react";
import { salaryStructureService, flexiBenefitsService } from "../../../../services/api";

const CATEGORY_OPTIONS = [
  { id: "earning", label: "Earning", value: "EARNING" },
  { id: "deduction", label: "Deduction", value: "DEDUCTION" },
  { id: "benefit", label: "Benefit", value: "BENEFIT" },
  { id: "reimbursement", label: "Reimbursement", value: "REIMBURSEMENT" },
];

const FLEXI_FLAG_KEYS = [
  { name: "declarationRequired", label: "Employee must declare (opt-in)" },
  { name: "claimRequired", label: "Employee must claim with bills" },
  { name: "billRequired", label: "Supporting bill / proof required" },
  { name: "approvalRequired", label: "Needs approval before pay" },
];

function parseCcSlabsJson(raw) {
  if (!raw || !String(raw).trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Create / Edit salary component — clearer Reimbursement + Flexi flow.
 * Remounts only Flexi fields when TDS head changes (basics stay intact).
 */
export default function SalaryComponentFormModal({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  darkMode = false,
  title = "Create New Component",
  submitLabel = "Create Component",
}) {
  const [tdsHeads, setTdsHeads] = useState([]);
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState("EARNING");
  const [tdsHeadId, setTdsHeadId] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [headPanels, setHeadPanels] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [showItDetail, setShowItDetail] = useState(false);
  const [showDeclFields, setShowDeclFields] = useState(false);

  const [flexiEnabled, setFlexiEnabled] = useState(false);
  const [payMethod, setPayMethod] = useState("COLLECT_THEN_CLAIM");
  const [annualMax, setAnnualMax] = useState("");
  const [monthlyMax, setMonthlyMax] = useState("");
  const [annualMaxFormula, setAnnualMaxFormula] = useState("");
  const [basicMult, setBasicMult] = useState("1");
  const [accumulation, setAccumulation] = useState(true);
  const [mealPerDay, setMealPerDay] = useState("");
  const [mealMonthly, setMealMonthly] = useState("");
  const [ccSlabsText, setCcSlabsText] = useState("[]");
  const [flags, setFlags] = useState({
    declarationRequired: true,
    claimRequired: true,
    billRequired: true,
    approvalRequired: true,
  });
  const [guidelines, setGuidelines] = useState({});
  const [declarationSchema, setDeclarationSchema] = useState([]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await salaryStructureService.listTdsReferenceHeads();
        if (!cancelled) setTdsHeads(res?.data || []);
      } catch {
        if (!cancelled) setTdsHeads([]);
      }
    })();

    setName(initialData?.name || "");
    setSortOrder(
      initialData?.sortOrder != null
        ? String(initialData.sortOrder)
        : initialData?.orderNo != null
          ? String(initialData.orderNo)
          : ""
    );
    setNotes(initialData?.notes || "");
    setCategory(initialData?.category || "EARNING");
    setTdsHeadId(
      initialData?.tdsReferenceHeadId ? String(initialData.tdsReferenceHeadId) : ""
    );
    setShowInfo(false);
    setShowItDetail(false);
    setShowDeclFields(false);
    return () => {
      cancelled = true;
    };
  }, [isOpen, initialData?.id, initialData?.tdsReferenceHeadId, initialData?.category]);

  useEffect(() => {
    if (category === "REIMBURSEMENT") setFlexiEnabled(true);
  }, [category]);

  useEffect(() => {
    if (!isOpen || !tdsHeadId) {
      setHeadPanels(null);
      return undefined;
    }
    const head = tdsHeads.find((h) => String(h.id) === String(tdsHeadId));
    const code = head?.code;
    let cancelled = false;
    (async () => {
      try {
        const res = await flexiBenefitsService.getHeadTemplate(code || "OTHER", {
          headId: tdsHeadId,
        });
        if (cancelled) return;
        const data = res?.data || null;
        setHeadPanels(data);
        const saved = initialData?.flexiConfigJson || initialData?.flexiConfig;
        const sameHead =
          saved?.statutory?.headCode === code ||
          saved?.headCode === code ||
          (initialData?.tdsReferenceHeadId &&
            String(initialData.tdsReferenceHeadId) === String(tdsHeadId));
        const tmpl =
          saved && sameHead
            ? { ...data?.companyTemplate, ...saved }
            : data?.companyTemplate || {};

        setFlexiEnabled(
          category === "REIMBURSEMENT"
            ? true
            : initialData?.flexiEnabled != null
              ? !!initialData.flexiEnabled
              : !!tmpl.enabled
        );
        setPayMethod(tmpl.payMethod || data?.companyUi?.defaultPayMethod || "COLLECT_THEN_CLAIM");
        setAnnualMax(tmpl.annualMax != null ? String(tmpl.annualMax) : "");
        setMonthlyMax(tmpl.monthlyMax != null ? String(tmpl.monthlyMax) : "");
        setAnnualMaxFormula(tmpl.annualMaxFormula || "");
        setBasicMult(
          tmpl.annualMaxBasicMultiplier != null
            ? String(tmpl.annualMaxBasicMultiplier)
            : "1"
        );
        setAccumulation(tmpl.accumulationEnabled !== false);
        setMealPerDay(tmpl.mealPerDayCap != null ? String(tmpl.mealPerDayCap) : "");
        setMealMonthly(tmpl.mealMonthlyCap != null ? String(tmpl.mealMonthlyCap) : "");
        setCcSlabsText(JSON.stringify(tmpl.ccSlabs || data?.companyTemplate?.ccSlabs || [], null, 2));
        setFlags({
          declarationRequired: tmpl.declarationRequired !== false,
          claimRequired: tmpl.claimRequired !== false,
          billRequired: tmpl.billRequired !== false,
          approvalRequired: tmpl.approvalRequired !== false,
        });
        setGuidelines(tmpl.guidelines || data?.companyTemplate?.guidelines || {});
        setDeclarationSchema(
          tmpl.declarationSchema || data?.declarationSchema || []
        );
      } catch {
        if (!cancelled) setHeadPanels(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    tdsHeadId,
    tdsHeads,
    category,
    initialData?.flexiConfigJson,
    initialData?.flexiEnabled,
    initialData?.tdsReferenceHeadId,
  ]);

  const selectedHead = useMemo(
    () => tdsHeads.find((h) => String(h.id) === String(tdsHeadId)),
    [tdsHeads, tdsHeadId]
  );
  const statutory = headPanels?.statutory;
  const ui = headPanels?.companyUi || {};
  const showFlexi = category === "REIMBURSEMENT" || flexiEnabled;
  const annualNum = annualMax !== "" ? Number(annualMax) : null;
  const itCapWarn = ui.itCapWarning != null ? Number(ui.itCapWarning) : null;

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    const headId = Number(tdsHeadId);
    if (!trimmed || !category) return;
    if (!headId) return;

    const tmpl = headPanels?.companyTemplate || {};
    const ccSlabs = parseCcSlabsJson(ccSlabsText) || tmpl.ccSlabs || null;
    const enabled = category === "REIMBURSEMENT" ? true : flexiEnabled;

    const flexiConfig = {
      ...tmpl,
      enabled,
      headCode: headPanels?.headCode || tmpl.headCode || null,
      payMethod: payMethod || "COLLECT_THEN_CLAIM",
      declarationRequired: !!flags.declarationRequired,
      claimRequired: !!flags.claimRequired,
      billRequired: !!flags.billRequired,
      approvalRequired: !!flags.approvalRequired,
      annualMax:
        ui.showAnnualMax === false
          ? tmpl.annualMax ?? null
          : annualMax !== ""
            ? Number(annualMax)
            : null,
      monthlyMax:
        ui.showMonthlyMax === false
          ? tmpl.monthlyMax ?? null
          : monthlyMax !== ""
            ? Number(monthlyMax)
            : null,
      annualMaxFormula:
        ui.showBasicFormula === false ? null : annualMaxFormula || null,
      annualMaxBasicMultiplier:
        ui.showBasicFormula === false
          ? null
          : basicMult !== ""
            ? Number(basicMult)
            : tmpl.annualMaxBasicMultiplier ?? null,
      accumulationEnabled: ui.showAccumulation === false ? false : accumulation,
      mealPerDayCap:
        ui.showMealCaps && mealPerDay !== ""
          ? Number(mealPerDay)
          : tmpl.mealPerDayCap ?? null,
      mealMonthlyCap:
        ui.showMealCaps && mealMonthly !== ""
          ? Number(mealMonthly)
          : tmpl.mealMonthlyCap ?? null,
      ccSlabs: ui.showCcSlabs ? ccSlabs : tmpl.ccSlabs || null,
      balanceTracking: true,
      payrollIntegration: true,
      workflowCode: "reimbursement",
      declarationSchema: declarationSchema.length
        ? declarationSchema
        : tmpl.declarationSchema || headPanels?.declarationSchema || [],
      guidelines: guidelines || tmpl.guidelines || {},
      statutory: headPanels?.statutory || tmpl.statutory || null,
      companyUi: ui,
    };

    onSubmit({
      name: trimmed,
      category,
      notes: notes.trim(),
      sortOrder: sortOrder === "" ? undefined : Number(sortOrder),
      tdsReferenceHeadId: headId,
      flexiEnabled: enabled,
      flexiConfigJson: flexiConfig,
    });
  };

  const suggestHead = async () => {
    if (!name.trim()) return;
    setSuggesting(true);
    try {
      const res = await salaryStructureService.suggestTdsReferenceHead({
        name: name.trim(),
        category,
      });
      const id = res?.data?.head?.id;
      if (id) setTdsHeadId(String(id));
    } catch {
      /* ignore */
    } finally {
      setSuggesting(false);
    }
  };

  const panelBg = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[#1a1a1a] text-gray-100"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";
  const fieldCls = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[#2C2C2C] text-white"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";
  const labelCls = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]";
  const mutedCls = "text-[hsl(var(--ds-muted-fg))]";
  const inputBase =
    "w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-ring))]";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        className={`flex max-h-[min(94vh,920px)] w-full max-w-5xl flex-col overflow-hidden rounded-xl border shadow-2xl ${panelBg}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="salary-component-form-title"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[hsl(var(--ds-border))] px-5 py-4 sm:px-6">
          <div className="flex min-w-0 items-start gap-2">
            <div className="min-w-0">
              <h3
                id="salary-component-form-title"
                className="text-base font-semibold tracking-tight"
              >
                {title}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setShowInfo((v) => !v)}
              className={`mt-0.5 shrink-0 rounded-full p-1 ${mutedCls} hover:bg-[hsl(var(--ds-muted)/0.35)]`}
              aria-label="Component form information"
            >
              <Info size={16} strokeWidth={1.75} />
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-1.5"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
            {showInfo && (
              <div
                className={`rounded-xl border px-3 py-2.5 text-xs leading-relaxed ${
                  darkMode
                    ? "border-gray-600 bg-gray-800/80 text-gray-200"
                    : "border-gray-200 bg-gray-50 text-gray-700"
                }`}
              >
                <p className="mb-1 font-medium">How this works</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>
                    Pick a <strong>TDS Reference Head</strong> first — IT Act rules come from
                    that head (read-only). Company Flexi ceilings are set below.
                  </li>
                  <li>
                    Category <strong>Reimbursement</strong> turns on Flexi declare/claim for
                    employees (Payroll → Reimbursement).
                  </li>
                  <li>
                    When you add this component to a salary structure with an amount, it{" "}
                    <strong>uses CTC room</strong> — Special Allowance (or other Balance CTC
                    component) reduces automatically. Set the structure amount there, not only
                    here.
                  </li>
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              <div className="sm:col-span-8">
                <label className={`mb-1.5 block text-sm font-medium ${labelCls}`}>
                  Component Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="e.g. Mobile Allowance, Fuel Reimbursement"
                  className={`${inputBase} ${fieldCls}`}
                />
              </div>
              <div className="sm:col-span-4">
                <label className={`mb-1.5 block text-sm font-medium ${labelCls}`}>Order No.</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className={`${inputBase} ${fieldCls}`}
                />
              </div>
            </div>

            <div>
              <label className={`mb-1.5 block text-sm font-medium ${labelCls}`}>
                Category <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {CATEGORY_OPTIONS.map((opt) => {
                  const selected = category === opt.value;
                  return (
                    <label
                      key={opt.id}
                      className={`flex cursor-pointer items-center justify-center rounded-lg border px-3 py-2.5 text-sm font-medium ${
                        selected
                          ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))]"
                          : "border-[hsl(var(--ds-border))]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="componentCategory"
                        value={opt.value}
                        checked={selected}
                        onChange={() => setCategory(opt.value)}
                        className="sr-only"
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
              {category === "REIMBURSEMENT" ? (
                <p className={`mt-2 text-[11px] ${mutedCls}`}>
                  In salary structure, this line takes CTC. Balancing components (e.g. Special
                  Allowance) absorb the leftover CTC after earnings + reimbursements + benefits.
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className={`text-sm font-medium ${labelCls}`}>
                    TDS Reference Head <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={suggestHead}
                    disabled={suggesting || !name.trim()}
                    className="text-[11px] font-medium text-[hsl(var(--ds-primary))] disabled:opacity-40"
                  >
                    {suggesting ? "Suggesting…" : "Auto-suggest"}
                  </button>
                </div>
                <select
                  required
                  value={tdsHeadId}
                  onChange={(e) => setTdsHeadId(e.target.value)}
                  className={`${inputBase} ${fieldCls}`}
                >
                  <option value="">Select TDS Reference Head…</option>
                  {tdsHeads.map((h) => (
                    <option key={h.id} value={h.id}>
                      [{h.headGroup}] {h.name}
                      {h.exemptionSection ? ` · ${h.exemptionSection}` : ""}
                      {h.maxAnnualLimit != null ? ` · IT ₹${h.maxAnnualLimit}` : ""}
                    </option>
                  ))}
                </select>
                {selectedHead ? (
                  <p className={`mt-1 text-[11px] ${mutedCls}`}>
                    Head <span className="font-mono font-medium">{selectedHead.code}</span>
                    {" — "}changing head reloads IT rules and company Flexi defaults (name &amp;
                    category stay).
                  </p>
                ) : null}
              </div>
              <div className="lg:col-span-5">
                <label className={`mb-1.5 block text-sm font-medium ${labelCls}`}>Notes</label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal note (optional)"
                  className={`${inputBase} ${fieldCls}`}
                />
              </div>
            </div>

            {/* Compact IT statutory */}
            {statutory ? (
              <div className="rounded-xl border border-[hsl(var(--ds-info)/0.35)] bg-[hsl(var(--ds-info)/0.06)] p-3 sm:p-4">
                <button
                  type="button"
                  onClick={() => setShowItDetail((v) => !v)}
                  className="flex w-full items-center justify-between gap-2 text-left"
                >
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
                    <Lock size={14} className="text-[hsl(var(--ds-info))]" />
                    IT rules (from TDS head)
                  </span>
                  {showItDetail ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                <p className={`mt-1 text-[11px] ${mutedCls}`}>
                  {statutory.taxability || "—"}
                  {statutory.section ? ` · ${statutory.section}` : ""}
                  {" · "}
                  {statutory.itAnnualCap != null
                    ? `IT cap ₹${statutory.itAnnualCap}/yr`
                    : "No fixed Act ₹ cap"}
                  {statutory.proofRequired ? " · Proof required" : ""}
                </p>
                {showItDetail ? (
                  <div className="mt-3 space-y-2 border-t border-[hsl(var(--ds-border))] pt-3">
                    <p className={`text-xs ${mutedCls}`}>{statutory.summary}</p>
                    <ul className={`list-disc space-y-1 pl-4 text-xs ${mutedCls}`}>
                      {(statutory.conditions || []).map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                    <p className={`inline-flex items-start gap-1 text-[11px] ${mutedCls}`}>
                      <ExternalLink size={12} className="mt-0.5 shrink-0" />
                      Change IT rules at: Payroll → Settings → Statutory Rates → TDS Reference
                      Heads
                    </p>
                  </div>
                ) : null}
              </div>
            ) : tdsHeadId ? (
              <p className={`text-xs ${mutedCls}`}>Loading IT rules for selected head…</p>
            ) : null}

            {/* Company Flexi / Reimbursement policy */}
            {showFlexi ? (
              <div className="rounded-xl border border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.06)] p-4 sm:p-5">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold">
                      Company policy — {ui.title || "Flexi / Reimbursement"}
                    </p>
                    <p className={`mt-0.5 text-[11px] ${mutedCls}`}>
                      Employer ceilings and claim flow. Does not override IT Act limits above.
                    </p>
                  </div>
                  {category !== "REIMBURSEMENT" ? (
                    <label className="inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-xs font-medium">
                      <input
                        type="checkbox"
                        checked={flexiEnabled}
                        onChange={(e) => setFlexiEnabled(e.target.checked)}
                      />
                      Enable Flexi on this component
                    </label>
                  ) : (
                    <span className="rounded-full bg-[hsl(var(--ds-warning)/0.15)] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--ds-warning))]">
                      Flexi on for Reimbursement
                    </span>
                  )}
                </div>

                {(category === "REIMBURSEMENT" || flexiEnabled) && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                      <div className="lg:col-span-6">
                        <label className={`mb-1.5 block text-xs ${mutedCls}`}>
                          How employees get money
                        </label>
                        <select
                          value={payMethod}
                          onChange={(e) => setPayMethod(e.target.value)}
                          className={`${inputBase} ${fieldCls}`}
                        >
                          <option value="COLLECT_THEN_CLAIM">
                            Method A — Claim with bills, then pay (recommended)
                          </option>
                          <option value="CLAIM_THEN_COLLECT">
                            Method B — Paid via salary first; bills for tax proof
                          </option>
                        </select>
                      </div>
                      {ui.showAnnualMax !== false ? (
                        <div className="lg:col-span-3">
                          <label className={`mb-1.5 block text-xs ${mutedCls}`}>
                            Company annual max (₹)
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={annualMax}
                            onChange={(e) => setAnnualMax(e.target.value)}
                            className={`${inputBase} ${fieldCls}`}
                          />
                          {itCapWarn != null &&
                          annualNum != null &&
                          Number.isFinite(annualNum) &&
                          annualNum > itCapWarn ? (
                            <p className="mt-1 text-[10px] text-[hsl(var(--ds-danger))]">
                              Above IT cap ₹{itCapWarn} — excess is taxable
                            </p>
                          ) : itCapWarn != null ? (
                            <p className={`mt-1 text-[10px] ${mutedCls}`}>
                              IT reference cap ₹{itCapWarn}/yr
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                      {ui.showMonthlyMax ? (
                        <div className="lg:col-span-3">
                          <label className={`mb-1.5 block text-xs ${mutedCls}`}>
                            Company monthly max (₹)
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={monthlyMax}
                            onChange={(e) => setMonthlyMax(e.target.value)}
                            className={`${inputBase} ${fieldCls}`}
                          />
                        </div>
                      ) : null}
                    </div>

                    {ui.showBasicFormula ? (
                      <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
                        <p className="mb-2 text-[11px] font-semibold text-[hsl(var(--ds-warning))]">
                          LTA employer formula
                        </p>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className={`mb-1.5 block text-xs ${mutedCls}`}>
                              Annual formula
                            </label>
                            <select
                              value={annualMaxFormula}
                              onChange={(e) => setAnnualMaxFormula(e.target.value)}
                              className={`${inputBase} ${fieldCls}`}
                            >
                              <option value="">Fixed amount only</option>
                              <option value="N_TIMES_BASIC">N × monthly Basic</option>
                            </select>
                          </div>
                          <div>
                            <label className={`mb-1.5 block text-xs ${mutedCls}`}>
                              Basic multiplier (N)
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              value={basicMult}
                              onChange={(e) => setBasicMult(e.target.value)}
                              className={`${inputBase} ${fieldCls}`}
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {ui.showCcSlabs ? (
                      <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
                        <p className="mb-1 text-[11px] font-semibold text-[hsl(var(--ds-warning))]">
                          Fuel — company CC slab table
                        </p>
                        <p className={`mb-2 text-[10px] ${mutedCls}`}>
                          Employer payout rates by engine CC (JSON). Not the Rule 3 perquisite
                          table.
                        </p>
                        <textarea
                          rows={4}
                          value={ccSlabsText}
                          onChange={(e) => setCcSlabsText(e.target.value)}
                          className={`${inputBase} font-mono text-[11px] ${fieldCls}`}
                          spellCheck={false}
                        />
                      </div>
                    ) : null}

                    {ui.showMealCaps ? (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label className={`mb-1.5 block text-xs ${mutedCls}`}>
                            Company ₹ / meal (or day)
                          </label>
                          <input
                            type="number"
                            value={mealPerDay}
                            onChange={(e) => setMealPerDay(e.target.value)}
                            className={`${inputBase} ${fieldCls}`}
                          />
                        </div>
                        <div>
                          <label className={`mb-1.5 block text-xs ${mutedCls}`}>
                            Company monthly voucher cap
                          </label>
                          <input
                            type="number"
                            value={mealMonthly}
                            onChange={(e) => setMealMonthly(e.target.value)}
                            className={`${inputBase} ${fieldCls}`}
                          />
                        </div>
                      </div>
                    ) : null}

                    {ui.showAccumulation !== false ? (
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={accumulation}
                          onChange={(e) => setAccumulation(e.target.checked)}
                        />
                        Allow monthly accumulation (unused months can be claimed later)
                      </label>
                    ) : null}

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {FLEXI_FLAG_KEYS.map((f) => (
                        <label
                          key={f.name}
                          className="flex items-center gap-2 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-xs"
                        >
                          <input
                            type="checkbox"
                            checked={!!flags[f.name]}
                            onChange={(e) =>
                              setFlags((prev) => ({
                                ...prev,
                                [f.name]: e.target.checked,
                              }))
                            }
                          />
                          {f.label}
                        </label>
                      ))}
                    </div>

                    {declarationSchema.length > 0 ? (
                      <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
                        <button
                          type="button"
                          onClick={() => setShowDeclFields((v) => !v)}
                          className="flex w-full items-center justify-between text-left text-[11px] font-semibold"
                        >
                          Employee declaration fields ({declarationSchema.length})
                          {showDeclFields ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {showDeclFields ? (
                          <ul className={`mt-2 space-y-1 text-xs ${mutedCls}`}>
                            {declarationSchema.map((f) => (
                              <li key={f.key}>
                                <span className="font-medium text-[hsl(var(--ds-fg))]">
                                  {f.label}
                                </span>
                                {f.required ? " *" : ""}
                                <span className="ml-1 opacity-70">({f.type})</span>
                              </li>
                            ))}
                            {guidelines?.declare ? (
                              <li className="pt-1">Declare: {guidelines.declare}</li>
                            ) : null}
                            {guidelines?.claim ? <li>Claim: {guidelines.claim}</li> : null}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-[hsl(var(--ds-border))] px-4 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-[hsl(var(--ds-primary))] px-5 py-2 text-sm font-medium text-white"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
